import {
  IconAtWork,
  IconHelpCircle,
  IconHome,
  IconLogOut,
  IconSettings,
} from '@mc/certificates-design-system'
import { useTranslation } from '@mc/i18n'
import Nav from '@mc/navigation'
import { paths } from '@mc/paths'
import { useFlw1235EnterpriseNavCta } from 'experiments/flw-1235_v2_enterprise_nav_cta'
import { useIsEnterpriseMember } from 'pages/certificates/common'
import { useFireEvent } from 'pages/certificates/tracking'

export interface NavigationAction {
  action: string
  level: 'primary' | 'secondary'
}

export const ACTIONS: { [key: string]: NavigationAction } = {
  LOGO: {
    action: 'MC Logo',
    level: 'primary',
  },
  MENU: {
    action: 'Menu',
    level: 'primary',
  },
  HOMEPAGE: {
    action: 'Homepage',
    level: 'secondary',
  },
  AT_WORK: {
    action: 'At Work',
    level: 'secondary',
  },
  SETTINGS: {
    action: 'Settings',
    level: 'secondary',
  },
  HELP_CENTER: {
    action: 'Help Center',
    level: 'secondary',
  },
  SIGN_OUT: {
    action: 'Sign Out',
    level: 'secondary',
  },
}

export interface NavigationMenuProps {
  /** Applied to the hamburger toggle button. */
  className?: string
  /** Applied to the expanded menu panel. */
  contentClassName?: string
  onSignOut?: (e: React.MouseEvent) => void
  placement?: React.ComponentProps<typeof Nav.Menu>['placement']
}

export const NavigationMenu = ({
  className = 'ml-4',
  contentClassName,
  onSignOut,
  placement,
}: NavigationMenuProps) => {
  const { t } = useTranslation('@mc/next')
  const fireEvent = useFireEvent()
  const isEnterprise = useIsEnterpriseMember()
  const atWorkCopy = useFlw1235EnterpriseNavCta(t('nav.cta.work'), isEnterprise)

  const handleMenuToggle = () => {
    fireEvent('certificateNavigationClicked', {
      action: ACTIONS.MENU.action,
      level: ACTIONS.MENU.level,
      location: 'nav bar',
    })
  }

  const handleNavigationClick = ({ action, level }: NavigationAction) => {
    fireEvent('certificateNavigationClicked', {
      action,
      level,
      location: 'nav bar',
    })
  }

  return (
    <Nav.Menu
      ariaLabel={t('nav.menu')}
      className={className}
      contentClassName={contentClassName}
      onToggle={handleMenuToggle}
      placement={placement}
    >
      <Nav.MenuLink
        className='p-4 block c-body-bold'
        href={paths.certificatesHomepage}
        onClick={() => handleNavigationClick(ACTIONS.HOMEPAGE)}
      >
        <div className='flex items-center gap-3'>
          <IconHome data-testid='icon-home' size='scale-8' />
          {t('nav.cta.home')}
        </div>
      </Nav.MenuLink>

      <Nav.MenuLink
        className='p-4 block c-body-bold'
        href={paths.helpCenter}
        onClick={() => handleNavigationClick(ACTIONS.HELP_CENTER)}
      >
        <div className='flex items-center gap-3'>
          <IconHelpCircle data-testid='icon-help-circle' size='scale-8' />
          {t('nav.cta.help')}
        </div>
      </Nav.MenuLink>

      {isEnterprise && (
        <Nav.MenuLink
          className='p-4 block c-body-bold'
          href={paths.home}
          onClick={() =>
            handleNavigationClick({ ...ACTIONS.AT_WORK, action: atWorkCopy })
          }
        >
          <div className='flex items-center gap-3'>
            <IconAtWork data-testid='icon-at-work' size='scale-8' />
            {atWorkCopy}
          </div>
        </Nav.MenuLink>
      )}

      <Nav.MenuLink
        className='p-4 block c-body-bold'
        href={paths.settings}
        onClick={() => handleNavigationClick(ACTIONS.SETTINGS)}
      >
        <div className='flex items-center gap-3'>
          <IconSettings data-testid='icon-settings' size='scale-8' />
          {t('nav.cta.settings')}
        </div>
      </Nav.MenuLink>

      <Nav.MenuLink
        className='p-4 block c-body-bold'
        onClick={(e) => {
          if (onSignOut) {
            onSignOut(e)
          }
          handleNavigationClick(ACTIONS.SIGN_OUT)
        }}
      >
        <div className='flex items-center gap-3'>
          <IconLogOut data-testid='icon-log-out' size='scale-8' />
          {t('nav.cta.logout')}
        </div>
      </Nav.MenuLink>
    </Nav.Menu>
  )
}
